import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { memo, useState } from 'react';

import { Button } from '@doger/ui/components/ui/button';
import { Card } from '@doger/ui/components/ui/card';
import { Skeleton } from '@doger/ui/components/ui/skeleton';

import { $$privateApi } from '@doger/admin/shared/api';

import { AddNoteModal } from '../add-note-modal';
import { NotesModal } from '../notes-modal';
import styles from './latest-note.module.scss';

type LatestNoteProps = {
  userId: string;
};

export const LatestNote = memo<LatestNoteProps>(({ userId }) => {
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);

  const {
    data: notesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-notes', userId, 'latest'],
    queryFn: () => $$privateApi.findManyNote({ userId, take: 1, skip: 0 }),
    enabled: !!userId,
  });

  const latestNote = notesData?.[0];

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <div className={styles.header}>
          <h3>Notes</h3>
          <Skeleton className={styles.buttonSkeleton} />
        </div>
        <Skeleton className={styles.contentSkeleton} />
      </Card>
    );
  }

  if (error) {
    return (
      <>
        <Card className={styles.card}>
          <div className={styles.header}>
            <h3>Notes</h3>
            <div className={styles.buttons}>
              <Button
                variant='contained'
                palette='primary'
                size='sm'
                onClick={() => setIsAddNoteModalOpen(true)}
              >
                Add Note
              </Button>
              <Button
                variant='outlined'
                palette='primary'
                size='sm'
                onClick={() => setIsNotesModalOpen(true)}
              >
                View All Notes
              </Button>
            </div>
          </div>
          <div className={styles.error}>
            Error loading notes: {error.message}
          </div>
        </Card>

        <AddNoteModal
          userId={userId}
          isOpen={isAddNoteModalOpen}
          onClose={() => setIsAddNoteModalOpen(false)}
        />

        <NotesModal
          userId={userId}
          isOpen={isNotesModalOpen}
          onClose={() => setIsNotesModalOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <Card className={styles.card}>
        <div className={styles.header}>
          <h3>Notes</h3>
          <div className={styles.buttons}>
            <Button
              variant='contained'
              palette='primary'
              size='sm'
              onClick={() => setIsAddNoteModalOpen(true)}
            >
              Add Note
            </Button>
            <Button
              variant='outlined'
              palette='primary'
              size='sm'
              onClick={() => setIsNotesModalOpen(true)}
            >
              View All Notes
            </Button>
          </div>
        </div>

        {latestNote ? (
          <div className={styles.noteContent}>
            <div className={styles.noteMeta}>
              <span className={styles.creator}>
                Created by:{' '}
                {!!latestNote.Creator ? latestNote.Creator.email : 'N/A'}
              </span>
              <span className={styles.date}>
                {format(new Date(latestNote.createdAt), 'MMM dd, yyyy HH:mm')}
              </span>
            </div>
            <div className={styles.noteText}>{latestNote.content}</div>
          </div>
        ) : (
          <div className={styles.empty}>No notes found for this user.</div>
        )}
      </Card>

      <NotesModal
        userId={userId}
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
      />

      <AddNoteModal
        userId={userId}
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
      />
    </>
  );
});
